// Town portal / "recall" (B5, monorail-w77; overlaps monorail-bal's
// portal half). Academically: Chemotaxis — the specimen homes along a
// gradient it apparently remembers. Innate active on T, Diablo semantics:
//
//   cast (afield, off cooldown) → travel home instantly, leaving a linked
//   gate pair — one swirl at the cast spot, one by the Mother. Swimming
//   into the home gate makes the return trip and CLOSES the pair (a
//   standing deep→home wormhole would retire the throat/shortcut loop;
//   the round trip is the QoL, not a commuter line). Recasting moves the
//   pair; death clears it with the world (your portal died with you).
//
// Gates render through the hazard pipeline as kind 'portal' — a
// dedicated branch in hazard.slang matched to the D2 reference: an
// upright oval ring of crackling blue flame around a dark void.
// hazardSystem has no 'portal' branch, so contact stays harmless;
// teleport lives here.

import type { Entity, Vec2 } from '../components'
import type { GameWorld } from '../world'
import type { GameInput } from '../controls'
import type { EventBus } from '../events'
import { motherPosition, MOTHER_HOME } from './npc'
import { inWater } from '../dungeon/depth-field'
import { emitRipple, spawnPigmentPuff } from './particles'
import { playSfx } from '../../engine/audio'
import { T } from '../tunables'

// Classic town-portal blue — arcane, unmistakable, nothing else in the
// water burns this hue (currents are dusty ink-blue washes; this is the
// saturated gate blue, and the shader adds the white-hot eye).
const PORTAL_TINT: [number, number, number] = [0.45, 0.55, 1.0]

function spawnGate(world: GameWorld, at: Vec2, side: 'field' | 'home', exit: Vec2): void {
  world.add({
    position: { x: at.x, y: at.y },
    wobble: { phase: Math.random() * Math.PI * 2, freq: 0.8, amp: 0.05 },
    renderable: { shape: 'current', radius: T.skills.portal.gateRadius, tint: PORTAL_TINT },
    hazard: { kind: 'portal' },
    portalGate: { side, exit: { x: exit.x, y: exit.y } },
  })
}

/** Splash both ends of a trip — departure ripple, arrival ripple + puff. */
function stageTrip(world: GameWorld, from: Vec2, to: Vec2): void {
  emitRipple(from.x, from.y, { strength: 0.45, lifetime: 0.8 })
  emitRipple(to.x, to.y, { strength: 0.45, lifetime: 0.8 })
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2
    spawnPigmentPuff(world, to.x + Math.cos(a) * 0.1, to.y + Math.sin(a) * 0.1, PORTAL_TINT, 0.05)
  }
  playSfx('dash_whoosh', { volume: 0.5 })
}

function clearGates(world: GameWorld): void {
  for (const gate of [...world.with('portalGate')]) {
    if (world.id(gate) !== undefined) world.remove(gate)
  }
}

/** Home-side gate spot: beside the Mother, never on top of her (she is
 *  not a doorway), nudged around the compass if a bank is in the way. */
function homeGateSpot(world: GameWorld): Vec2 {
  const mom = motherPosition(world) ?? MOTHER_HOME
  const offsets: Array<[number, number]> = [[0, 1.0], [0, -1.0], [1.0, 0], [-1.0, 0], [0.7, 0.7]]
  for (const [dx, dy] of offsets) {
    if (inWater(mom.x + dx, mom.y + dy, T.skills.portal.gateRadius * 0.5)) {
      return { x: mom.x + dx, y: mom.y + dy }
    }
  }
  return { x: mom.x, y: mom.y + 1.0 }
}

export function portalSystem(world: GameWorld, player: Entity, input: GameInput, dt: number, events: EventBus): void {
  if (!player.portal) return
  const c = T.skills.portal
  player.portal.cooldownRemaining = Math.max(0, player.portal.cooldownRemaining - dt)

  // ── Gate contact: teleport on the overlap EDGE only ─────────────────────
  const pr = player.renderable?.radius ?? T.player.baseRadius
  let overlapping: Entity | null = null
  for (const gate of world.with('portalGate', 'position', 'renderable')) {
    const dx = gate.position.x - player.position.x
    const dy = gate.position.y - player.position.y
    // Generous heart-of-the-swirl reach; brushing the rim doesn't commit.
    const reach = pr + gate.renderable.radius * 0.55
    if (dx * dx + dy * dy < reach * reach) {
      overlapping = gate
      break
    }
  }
  if (overlapping && !player.portalOverlap) {
    const from = { x: player.position.x, y: player.position.y }
    const exit = overlapping.portalGate!.exit
    const homeward = overlapping.portalGate!.side === 'field'
    player.position.x = exit.x
    player.position.y = exit.y
    if (player.velocity) { player.velocity.x = 0; player.velocity.y = 0 }
    // Round trip complete: the home-side crossing folds the pair shut.
    if (!homeward) clearGates(world)
    stageTrip(world, from, exit)
    events.emit(homeward
      ? { type: 'portal.cast', x: exit.x, y: exit.y }
      : { type: 'portal.return', x: exit.x, y: exit.y })
  }
  player.portalOverlap = overlapping !== null

  // ── Cast ─────────────────────────────────────────────────────────────────
  if (!input.actions.justPressed('portal') || player.portal.cooldownRemaining > 0) return
  const home = homeGateSpot(world)
  const dist = Math.hypot(home.x - player.position.x, home.y - player.position.y)
  if (dist < c.minCastDist) {
    // Already home — a polite fizzle, no cooldown burned.
    spawnPigmentPuff(world, player.position.x, player.position.y, PORTAL_TINT, 0.04)
    return
  }

  clearGates(world) // recast moves the pair
  const fieldSpot = { x: player.position.x, y: player.position.y }
  spawnGate(world, fieldSpot, 'field', home)
  spawnGate(world, home, 'home', fieldSpot)

  player.position.x = home.x
  player.position.y = home.y
  if (player.velocity) { player.velocity.x = 0; player.velocity.y = 0 }
  // Arriving ON the home gate must not bounce straight back (edge latch).
  player.portalOverlap = true
  player.portal.cooldownRemaining = c.cooldown

  stageTrip(world, fieldSpot, home)
  events.emit({ type: 'portal.cast', x: home.x, y: home.y })
}
